"use client"

import { useState } from "react"
import { FieldPath, FieldValues, useFormContext, useWatch } from "react-hook-form"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { useSession } from "@/features/auth/context/session.context"
import { WorkScopeCompletePhotoEntry, WorkScopeCompletePhotosForScope } from "@/features/photos/api/photos.api"
import { useReadOnly } from "../context/read-only-context"
import {
  canRemoveWorkScopeCompleteOnHomeTab,
  isCompletableWorkScopeStatus,
  WORK_SCOPE_COMPLETE_PHOTO_WARNING,
} from "../lib/work-scope-complete"
import { useWorkScopeCompletePhotosContext } from "../context/work-scope-complete-photos-context"

type WorkScopeCompletionFieldsProps<TFieldValues extends FieldValues> = {
  statusFieldName: FieldPath<TFieldValues>
  dateFieldName: FieldPath<TFieldValues>
  photo: WorkScopeCompletePhotosForScope | null
  showDetails: boolean
}

function photoEntry(
  photo: WorkScopeCompletePhotosForScope | null,
  slot: "before" | "after",
): WorkScopeCompletePhotoEntry | null {
  if (!photo) return null
  if (slot === "before") return photo.before
  if (photo.photoId == null || !photo.thumbnailUrl) return null
  return {
    photoId: photo.photoId,
    thumbnailUrl: photo.thumbnailUrl,
    takenAt: photo.takenAt,
    taggedByUserId: photo.taggedByUserId,
    tagger: photo.tagger,
  }
}

export default function WorkScopeCompletionFields<TFieldValues extends FieldValues>({
  statusFieldName,
  dateFieldName,
  photo,
  showDetails,
}: WorkScopeCompletionFieldsProps<TFieldValues>) {
  const { control, register } = useFormContext<TFieldValues>()
  const dateRegistration = register(dateFieldName)
  const status = useWatch({ control, name: statusFieldName })
  const dateValue = useWatch({ control, name: dateFieldName })
  const { isReadOnly } = useReadOnly()
  const { session } = useSession()
  const context = useWorkScopeCompletePhotosContext()
  const [enlarged, setEnlarged] = useState<WorkScopeCompletePhotoEntry | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<WorkScopeCompletePhotoEntry | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)

  const statusIsComplete = isCompletableWorkScopeStatus(typeof status === "string" ? status : null)
  const dateEnabled = statusIsComplete && !isReadOnly
  const canRemove = canRemoveWorkScopeCompleteOnHomeTab(session?.role)
  const beforePhoto = photoEntry(photo, "before")
  const afterPhoto = photoEntry(photo, "after")
  const missingAfterPhoto = statusIsComplete && afterPhoto == null

  const handleRemove = async () => {
    if (!confirmRemove || !context) return
    setIsRemoving(true)
    setRemoveError(null)
    const result = await context.removeTag(confirmRemove.photoId)
    setIsRemoving(false)
    if (!result.success) {
      setRemoveError(result.message || "Failed to remove the tag")
      return
    }
    setConfirmRemove(null)
  }

  if (!showDetails) return null

  const thumbnails = [
    { slot: "before" as const, entry: beforePhoto, label: "Before" },
    { slot: "after" as const, entry: afterPhoto, label: "After" },
  ]

  return (
    <div className="flex flex-row flex-wrap items-end gap-3">
      <label className="flex w-44 shrink-0 flex-col gap-1 text-sm">
        <span>Date Completed</span>
        <input
          type="date"
          aria-label="Date Completed"
          disabled={!dateEnabled}
          name={dateRegistration.name}
          ref={dateRegistration.ref}
          onBlur={dateRegistration.onBlur}
          onChange={dateRegistration.onChange}
          value={typeof dateValue === "string" ? dateValue : ""}
          className="h-14 w-full rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
        />
      </label>
      {thumbnails.map(({ slot, entry, label }) => entry && (
        <div key={slot} className="flex items-center gap-2">
          <button
            type="button"
            className="overflow-hidden rounded border"
            onClick={() => setEnlarged(entry)}
            aria-label={`Enlarge ${slot} photo`}
          >
            <img
              src={entry.thumbnailUrl}
              alt={`${label} work scope photo`}
              className="h-16 w-auto object-cover"
            />
          </button>
          {canRemove && (
            <Button type="button" size="sm" variant="outline" onClick={() => setConfirmRemove(entry)}>
              {`Remove ${slot} photo`}
            </Button>
          )}
        </div>
      ))}
      {missingAfterPhoto && (
        <p role="status" className="pb-4 text-sm text-destructive">
          {WORK_SCOPE_COMPLETE_PHOTO_WARNING}
        </p>
      )}
      {removeError && (
        <p role="alert" className="text-xs text-destructive">
          {removeError}
        </p>
      )}
      <Dialog open={enlarged != null} onOpenChange={(open) => { if (!open) setEnlarged(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{enlarged === beforePhoto ? "Before photo" : "After photo"}</DialogTitle>
          </DialogHeader>
          {enlarged && (
            <img src={enlarged.thumbnailUrl} alt="Enlarged work scope photo" className="max-h-[70vh] w-full object-contain" />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={confirmRemove != null} onOpenChange={(open) => { if (!open) setConfirmRemove(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmRemove === beforePhoto ? "Remove before photo" : "Remove after photo"}
            </DialogTitle>
            <DialogDescription>
              This removes only the tag. The photo stays on the Photos tab.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmRemove(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleRemove()} disabled={isRemoving}>
              Remove tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
